/**
 * The small DOM kit every lesson widget is built from (`LN-SEED01`).
 *
 * Lesson HTML is sanitized, so a widget cannot ship a `<button>` or a `style`
 * attribute of its own: the authored block is a `div[data-widget]` with data
 * attributes and a readable fallback inside it, and everything interactive is
 * built here, after hydration, from the `pc-*` classes in
 * `styles/progsity-content.css`.
 *
 * Nothing in here writes `innerHTML`: every value out of a data attribute
 * reaches the page as `textContent`.
 *
 * The widget's own words (its label, the step buttons, the step counter) come
 * from the `lessons.widgets` messages through a `WidgetText` (`LN-WGT02`), so
 * `/bn` reads them in Bangla with Bangla digits; the data is the lesson's and
 * is drawn as written.
 */

/** A translator over the `lessons.widgets` messages (next-intl's `t`). */
export type WidgetText = (key: string, values?: Record<string, string | number>) => string;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** `JSON.parse` on a data attribute, with the widget's own shape check. */
export function parseJson<T>(raw: string | undefined, isValid: (value: unknown) => value is T): T | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export const isStringOrNumber = (v: unknown): v is string | number =>
  typeof v === "string" || typeof v === "number";

export const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

export const isNonEmptyString = (v: unknown): v is string => typeof v === "string" && v.length > 0;

export const isIndex = (v: unknown): v is number => Number.isInteger(v) && (v as number) >= 0;

export const asText = (v: string | number): string => String(v);

interface ShellOptions {
  /** The uppercase label in the widget header. */
  label: string;
  /** One line of code the widget is tracing, shown above the body. */
  code?: string;
  /** One sentence under the body. */
  caption?: string;
}

export interface WidgetShell {
  /** The widget itself, the authored host. */
  root: HTMLElement;
  /** Where the widget draws itself. */
  body: HTMLDivElement;
  /** Where a widget appends its controls; already in the header. */
  controls: HTMLDivElement;
}

/**
 * Replace the authored fallback with the widget frame.
 *
 * The fallback is what a reader sees before this runs (and if JavaScript never
 * runs at all), so it is only removed once the widget has valid data.
 */
export function buildShell(host: HTMLElement, options: ShellOptions): WidgetShell {
  host.textContent = "";
  host.classList.add("pc-widget");
  host.setAttribute("role", "group");
  host.setAttribute("aria-label", options.label);

  const head = el("div", "pc-widget-head");
  head.append(el("span", "pc-widget-label", options.label));
  const controls = el("div", "pc-widget-controls");
  head.append(controls);
  host.append(head);

  if (options.code) host.append(el("pre", "pc-widget-code", options.code));

  const body = el("div", "pc-widget-body");
  host.append(body);

  if (options.caption) host.append(el("p", "pc-widget-caption", options.caption));

  return { root: host, body, controls };
}

export function button(label: string, ariaLabel: string): HTMLButtonElement {
  const node = el("button", "pc-widget-btn", label);
  node.type = "button";
  node.setAttribute("aria-label", ariaLabel);
  return node;
}

/** The sentence under a frame that says what the step did, read out on change. */
export function liveNote(className = "pc-widget-note"): HTMLParagraphElement {
  const note = el("p", className);
  note.setAttribute("aria-live", "polite");
  return note;
}

interface StepperOptions {
  /** The readout between the buttons; the default is "2 / 5". */
  stepLabel?: (step: number) => string;
  /** The step the first frame shows. */
  initialStep?: number;
  /**
   * Whether the readout is the live region. A widget that writes its own
   * `liveNote` turns it off, so a screen reader hears the note once rather
   * than the counter and then the note.
   */
  liveReadout?: boolean;
}

export interface Stepper {
  /** Show a step (clamped to the range). */
  go: (step: number) => void;
  /** A new number of steps (a widget that switches what it steps through), back to step 0. */
  setSteps: (steps: number) => void;
}

/**
 * Previous / next over a fixed number of steps, and back to the first.
 *
 * There is no autoplay and no timer: the only motion in a widget is the CSS
 * transition between two states, and that sits behind
 * `prefers-reduced-motion: no-preference`. A reader who steps through by hand
 * sees the same frames either way. Left and right arrows step from anywhere in
 * the widget, Home and End jump to the ends.
 */
export function attachStepper(
  shell: WidgetShell,
  steps: number,
  render: (step: number) => void,
  text: WidgetText,
  options: StepperOptions = {},
): Stepper {
  let current = 0;
  let count = steps;
  const stepLabel = options.stepLabel ?? ((step: number) => text("step", { n: step + 1, total: count }));

  const reset = button("↺", text("reset"));
  reset.classList.add("pc-widget-btn--reset");
  const previous = button("‹", text("previous"));
  const readout = el("span", "pc-widget-step");
  if (options.liveReadout !== false) readout.setAttribute("aria-live", "polite");
  const next = button("›", text("next"));

  const go = (step: number) => {
    current = Math.min(Math.max(step, 0), count - 1);
    previous.disabled = current === 0;
    reset.disabled = current === 0;
    next.disabled = current === count - 1;
    reset.hidden = count < 3;
    readout.textContent = stepLabel(current);
    // A button that just turned itself off would drop the keyboard focus to
    // the page; hand it to the button that still goes somewhere.
    const focused = shell.root.ownerDocument.activeElement;
    if (focused instanceof HTMLButtonElement && focused.disabled) {
      (current === 0 ? next : previous).focus();
    }
    render(current);
  };

  reset.addEventListener("click", () => go(0));
  previous.addEventListener("click", () => go(current - 1));
  next.addEventListener("click", () => go(current + 1));
  shell.root.addEventListener("keydown", (event) => {
    const moves: Record<string, number> = {
      ArrowLeft: current - 1,
      ArrowRight: current + 1,
      Home: 0,
      End: count - 1,
    };
    if (!(event.key in moves)) return;
    event.preventDefault();
    go(moves[event.key]);
  });

  shell.controls.append(reset, previous, readout, next);
  const initial = options.initialStep ?? 0;
  go(Number.isInteger(initial) ? initial : 0);

  return {
    go,
    setSteps: (steps: number) => {
      count = steps;
      go(0);
    },
  };
}
